import { useEffect as useEffectPb, useMemo as useMemoPb, useRef as useRefPb, useState as useStatePb } from "react";
import { Api } from "./api";
import { Banner, Field, Icon } from "./components";

// Project Builder — residents draft an architectural review request,
// the app polishes it against the community's bylaws, and they submit
// a board-ready version. Managers see the queue once submitted.

const PB_SCOPE_LABEL = {
  deck: "Deck",
  fence: "Fence",
  paint: "Exterior paint",
  landscaping: "Landscaping",
  addition: "Addition",
  solar: "Solar",
  windows: "Windows",
  roof: "Roof",
  driveway: "Driveway",
  pool: "Pool / Spa",
  other: "Other",
};
const PB_SCOPE_ICON = {
  deck: "home", fence: "shield", paint: "spark", landscaping: "spark",
  addition: "building", solar: "spark", windows: "home", roof: "home",
  driveway: "arrow", pool: "spark", other: "tag",
};
const PB_STATUS_PILL = {
  draft: { text: "Draft", cls: "pill--ghost" },
  polished: { text: "Polished", cls: "pill--gold" },
  submitted: { text: "Submitted", cls: "pill--accent" },
  in_review: { text: "In review", cls: "pill--gold" },
  needs_changes: { text: "Needs changes", cls: "pill--red" },
  approved: { text: "Approved", cls: "pill--green" },
  rejected: { text: "Rejected", cls: "pill--red" },
};

function _relTimePb(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// CODE_REVIEW P2 L266 — routes through the shared Api.authedBlobUrl cache.
function ProjectImage({ imageId, alt = "", style }) {
  const [src, setSrc] = useStatePb(null);
  const [err, setErr] = useStatePb(false);
  useEffectPb(() => {
    if (!imageId) { setSrc(null); setErr(false); return; }
    let cancelled = false;
    let release = null;
    setErr(false);
    window.Api.authedBlobUrl(`/api/projects/images/${imageId}`).then(
      ({ url, release: r }) => {
        if (cancelled) { r(); return; }
        release = r;
        setSrc(url);
      },
      () => { if (!cancelled) setErr(true); },
    );
    return () => { cancelled = true; if (release) release(); };
  }, [imageId]);
  if (err || !src) return null;
  return <img src={src} alt={alt} style={style} />;
}

function ProjectBuilder({ user }) {
  const [view, setView] = useStatePb("list");
  const [activeId, setActiveId] = useStatePb(null);

  const open = (id) => { setActiveId(id); setView("detail"); };
  const newDraft = () => { setActiveId(null); setView("new"); };
  const back = () => { setView("list"); setActiveId(null); };

  if (view === "detail" && activeId) {
    return <ProjectDetail id={activeId} user={user} onBack={back} />;
  }
  if (view === "new") {
    return <ProjectWizard user={user} onCancel={back} onSaved={(p) => open(p.id)} />;
  }
  return <ProjectList user={user} onOpen={open} onNew={newDraft} />;
}

function ProjectList({ user, onOpen, onNew }) {
  const [state, setState] = useStatePb({ loading: true, error: null, projects: [] });
  const reload = () => {
    setState({ loading: true, error: null, projects: [] });
    window.Api.projectsList()
      .then((data) => setState({ loading: false, error: null, projects: data || [] }))
      .catch((err) => setState({ loading: false, error: err.message || "Couldn't load.", projects: [] }));
  };
  useEffectPb(() => { reload(); }, []);

  const community = user?.community?.name || "your community";
  const mine = state.projects.filter((p) => p.is_mine);
  const board = state.projects.filter((p) => !p.is_mine && p.can_moderate);

  return (
    <div className="scroll">
      <div className="stack-md">
        <Banner icon="spark" tone="accent">
          <b>Project Builder.</b> Draft a renovation idea, run a Compass polish to align
          it with {community}'s bylaws, then submit a board-ready packet. Higher
          alignment, fewer rounds of back-and-forth.
        </Banner>

        <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 16 }}>Your projects</div>
          <button className="btn btn--primary" onClick={onNew}>
            <Icon name="plus" size={16} /> &nbsp;Start a project
          </button>
        </div>

        {state.loading && <div className="card card--pad-lg" style={{ textAlign: "center", color: "var(--mute)" }}>Loading…</div>}
        {state.error && <div className="card card--pad-lg" style={{ color: "var(--danger)" }}>{state.error}</div>}
        {!state.loading && mine.length === 0 && (
          <div className="card card--pad-lg" style={{ textAlign: "center" }}>
            <div style={{ color: "var(--mute)", marginBottom: 14 }}>
              No projects yet. Start one — Compass will check it against your bylaws and help you
              put a cleaner submission in front of the board.
            </div>
            <button className="btn btn--primary" onClick={onNew}>Start a project</button>
          </div>
        )}
        {mine.length > 0 && (
          <div className="stack-sm">
            {mine.map((p) => <ProjectRow key={p.id} project={p} onClick={() => onOpen(p.id)} />)}
          </div>
        )}

        {board.length > 0 && (
          <>
            <div style={{ fontWeight: 600, fontSize: 16, marginTop: 16 }}>Board queue</div>
            <div className="text-sm text-mute" style={{ marginBottom: 6 }}>
              Submitted projects from residents in {community}.
            </div>
            <div className="stack-sm">
              {board.map((p) => <ProjectRow key={p.id} project={p} onClick={() => onOpen(p.id)} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ProjectRow({ project, onClick }) {
  const pill = PB_STATUS_PILL[project.status] || PB_STATUS_PILL.draft;
  const firstImage = (project.images || [])[0];
  return (
    <button type="button" onClick={onClick} className="card" style={{
      display: "flex", gap: 14, padding: 14, textAlign: "left", cursor: "pointer",
      alignItems: "center", border: "1px solid var(--hairline)", background: "var(--surface)",
    }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "var(--shadow-lg)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ""; }}>
      <div style={{ width: 56, height: 56, borderRadius: 10, background: "var(--bg-2)",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
        {firstImage
          ? <ProjectImage imageId={firstImage.id} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <Icon name={PB_SCOPE_ICON[project.scope] || "spark"} size={22} style={{ color: "var(--mute)" }} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>{project.title}</span>
          <span className={`pill ${pill.cls}`}>{pill.text}</span>
          {!project.is_mine && project.can_moderate && <span className="pill pill--accent">From {project.requester?.full_name || "resident"}</span>}
        </div>
        <div className="row" style={{ gap: 6, fontSize: 12, color: "var(--mute)", marginTop: 6 }}>
          <span>{PB_SCOPE_LABEL[project.scope] || project.scope}</span>
          <span>·</span>
          <span>{_relTimePb(project.updated_at)}</span>
          {project.polished_at && <><span>·</span><span>Polished {_relTimePb(project.polished_at)}</span></>}
        </div>
      </div>
    </button>
  );
}

/* ─── New-project wizard ─────────────────────────────────────────────── */
function ProjectWizard({ user, onCancel, onSaved }) {
  // Step 1: pick a scope. Step 2 = the basic details form. The
  // resident lands directly in the detail/edit view after save so
  // they can run polish + upload photos in one place.
  const [scope, setScope] = useStatePb("");
  const [title, setTitle] = useStatePb("");
  const [description, setDescription] = useStatePb("");
  const [busy, setBusy] = useStatePb(false);
  const [error, setError] = useStatePb(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setError(null);
    if (!scope) { setError("Pick a project type first."); return; }
    if (!title.trim()) { setError("Give the project a short title."); return; }
    setBusy(true);
    try {
      const saved = await window.Api.projectsCreate({
        title: title.trim(),
        scope,
        description: description.trim() || undefined,
      });
      onSaved(saved);
    } catch (err) {
      setError(err.message || "Couldn't create the project.");
      setBusy(false);
    }
  };

  return (
    <div className="scroll">
      <form className="stack-md" onSubmit={submit} style={{ maxWidth: 760 }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Start a project</h2>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>Cancel</button>
        </div>

        {error && (
          <div role="alert" style={{ padding: "10px 12px", borderRadius: 8, background: "var(--danger-soft, #fdecea)", color: "var(--danger, #b3261e)" }}>
            {error}
          </div>
        )}

        <div className="card card--pad-lg" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field label="What kind of project?" hint="We'll steer Compass toward the bylaws that govern this type of work.">
            <div className="row" style={{ flexWrap: "wrap", gap: 8 }}>
              {Object.entries(PB_SCOPE_LABEL).map(([k, l]) => (
                <button key={k} type="button" onClick={() => setScope(k)}
                  className="row" style={{
                    gap: 6, padding: "8px 14px", borderRadius: 999,
                    border: scope === k ? "2px solid var(--accent)" : "1px solid var(--hairline)",
                    background: scope === k ? "var(--accent-soft)" : "var(--surface)",
                    color: scope === k ? "var(--accent-ink)" : "var(--ink)",
                    fontSize: 13, fontWeight: scope === k ? 600 : 400, cursor: "pointer",
                  }}>
                  <Icon name={PB_SCOPE_ICON[k] || "tag"} size={14} style={{ opacity: 0.8 }} />
                  {l}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Project title" hint="A short label you'll recognize in the list later.">
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder='e.g. "Replace back-yard fence with 5-ft cedar"' maxLength={180} />
          </Field>

          <Field label="A few sentences about the project (optional)" hint="What, where, why. You can flesh it out on the next screen with photos + materials.">
            <textarea className="input" rows={4} value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Replacing the storm-damaged section along the south property line. Cedar pickets, semi-transparent stain, no taller than the existing posts."
              style={{ resize: "vertical", lineHeight: 1.5 }} />
          </Field>
        </div>

        <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? "Creating…" : "Create draft"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ─── Detail / editor / polish view ───────────────────────────────────── */
function ProjectDetail({ id, user, onBack }) {
  const [state, setState] = useStatePb({ loading: true, error: null, project: null });
  const [busy, setBusy] = useStatePb(false);
  const [polishing, setPolishing] = useStatePb(false);
  // Two error channels — page-level (save/delete) and polish-specific.
  // Polish errors get rendered inside PolishCard so the user sees the
  // feedback right next to the button they just pressed instead of
  // having to scroll up on mobile.
  const [error, setError] = useStatePb(null);
  const [polishError, setPolishError] = useStatePb(null);

  const reload = () => {
    setState({ loading: true, error: null, project: null });
    window.Api.projectsGet(id)
      .then((project) => setState({ loading: false, error: null, project }))
      .catch((err) => setState({ loading: false, error: err.message || "Couldn't load.", project: null }));
  };
  useEffectPb(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const project = state.project;

  const save = async (patch) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await window.Api.projectsUpdate(id, patch);
      setState({ loading: false, error: null, project: updated });
    } catch (err) {
      setError(err.message || "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };

  const runPolish = async () => {
    setPolishing(true);
    setPolishError(null);
    try {
      const updated = await window.Api.projectsPolish(id);
      setState({ loading: false, error: null, project: updated });
    } catch (err) {
      setPolishError(err.message || "Polish failed.");
    } finally {
      setPolishing(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this project? This cannot be undone.")) return;
    setBusy(true);
    try { await window.Api.projectsDelete(id); onBack(); }
    catch (err) { setError(err.message || "Couldn't delete."); setBusy(false); }
  };

  if (state.loading) return <div className="scroll"><div className="card card--pad-lg" style={{ textAlign: "center", color: "var(--mute)" }}>Loading…</div></div>;
  if (state.error || !project) return <div className="scroll"><div className="card card--pad-lg" style={{ color: "var(--danger)" }}>{state.error || "Not found."}</div></div>;

  const pill = PB_STATUS_PILL[project.status] || PB_STATUS_PILL.draft;
  const isOwner = project.is_mine;
  const isMod = project.can_moderate;
  const canEdit = project.can_edit;

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 980 }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <button className="btn btn--ghost btn--sm" onClick={onBack}>
            <Icon name="chevron" size={14} style={{ transform: "rotate(180deg)" }} /> &nbsp;Back to projects
          </button>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <span className={`pill ${pill.cls}`}>{pill.text}</span>
            {isOwner && canEdit && (
              <button className="btn btn--ghost btn--sm" onClick={onDelete} disabled={busy} style={{ color: "var(--danger)" }}>
                <Icon name="trash" size={14} /> &nbsp;Delete
              </button>
            )}
          </div>
        </div>

        <div>
          <div className="text-sm text-mute" style={{ marginBottom: 4 }}>
            {PB_SCOPE_LABEL[project.scope] || project.scope}
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 600, margin: 0, lineHeight: 1.2 }}>{project.title}</h1>
          <div className="text-sm text-mute" style={{ marginTop: 6 }}>
            By {project.requester?.full_name || "Resident"} · Updated {_relTimePb(project.updated_at)}
            {project.submitted_at && <> · Submitted {_relTimePb(project.submitted_at)}</>}
          </div>
        </div>

        {error && (
          <div role="alert" style={{ padding: "10px 12px", borderRadius: 8, background: "var(--danger-soft, #fdecea)", color: "var(--danger, #b3261e)" }}>
            {error}
          </div>
        )}

        {/* Editable details — only when the project is on the resident's side */}
        {canEdit ? (
          <DetailsCard project={project} onSave={save} busy={busy} />
        ) : (
          <DetailsReadOnly project={project} />
        )}

        <PhotosCard project={project} canEdit={canEdit} onChange={reload} />

        {/* Polish workflow */}
        {isOwner && (
          <PolishCard
            project={project}
            polishing={polishing}
            onRun={runPolish}
            canEdit={canEdit}
            error={polishError}
            onClearError={() => setPolishError(null)}
            isHouseholdPrimary={user?.is_household_primary !== false}
          />
        )}

        {/* The polished narrative + bylaw findings + concerns + checklist */}
        {project.polished_narrative && (
          <PolishedOutput
            project={project}
            canEdit={canEdit}
            onEdit={canEdit ? (val) => save({ polished_narrative: val }) : null}
            onReload={reload}
          />
        )}

        {/* Download a board-ready .docx packet. Anyone who can see the
            project can download it — moderators may want to print one
            for a board meeting. */}
        <DownloadCard project={project} />


        {/* Board actions for moderators on board-visible states */}
        {isMod && !isOwner && (project.status === "submitted" || project.status === "in_review" || project.status === "needs_changes") && (
          <BoardActions project={project} onAction={save} busy={busy} />
        )}

        {project.board_decision_notes && (
          <div className="card card--pad-lg">
            <div className="text-xs text-mute" style={{ marginBottom: 6, letterSpacing: 0.4, textTransform: "uppercase" }}>Board notes</div>
            <div style={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.55 }}>{project.board_decision_notes}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailsCard({ project, onSave, busy }) {
  // Inline editor; saves on blur to avoid noisy "save" buttons.
  // Keep local state so typing isn't flickery during the save round-trip.
  const [draft, setDraft] = useStatePb({
    description: project.description || "",
    location: project.location || "",
    materials: project.materials || "",
    dimensions: project.dimensions || "",
    color: project.color || "",
    timeline: project.timeline || "",
    contractor: project.contractor || "",
  });
  useEffectPb(() => {
    setDraft({
      description: project.description || "",
      location: project.location || "",
      materials: project.materials || "",
      dimensions: project.dimensions || "",
      color: project.color || "",
      timeline: project.timeline || "",
      contractor: project.contractor || "",
    });
  }, [project.id, project.updated_at]);

  const commitField = (key) => {
    if ((project[key] || "") === (draft[key] || "")) return;
    onSave({ [key]: draft[key] });
  };
  const setField = (key, v) => setDraft((cur) => ({ ...cur, [key]: v }));

  return (
    <div className="card card--pad-lg">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>Project details</div>
        {busy && <span className="text-xs text-mute">Saving…</span>}
      </div>
      <div className="stack-sm">
        <Field label="Description" hint="The more specifics you give, the tighter Compass can be in its analysis.">
          <textarea className="input" rows={4} value={draft.description}
            onChange={(e) => setField("description", e.target.value)}
            onBlur={() => commitField("description")}
            style={{ resize: "vertical", lineHeight: 1.5 }} />
        </Field>
        <div className="auth__form-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Location" hint='Where on the property? ("back yard south fence")'>
            <input className="input" value={draft.location} onChange={(e) => setField("location", e.target.value)} onBlur={() => commitField("location")} />
          </Field>
          <Field label="Dimensions">
            <input className="input" value={draft.dimensions} onChange={(e) => setField("dimensions", e.target.value)} onBlur={() => commitField("dimensions")} placeholder='5&apos;8" tall x 40&apos; long' />
          </Field>
          <Field label="Materials">
            <input className="input" value={draft.materials} onChange={(e) => setField("materials", e.target.value)} onBlur={() => commitField("materials")} placeholder="Cedar pickets, galvanized hardware" />
          </Field>
          <Field label="Color / finish">
            <input className="input" value={draft.color} onChange={(e) => setField("color", e.target.value)} onBlur={() => commitField("color")} placeholder="Semi-transparent walnut stain" />
          </Field>
          <Field label="Timeline">
            <input className="input" value={draft.timeline} onChange={(e) => setField("timeline", e.target.value)} onBlur={() => commitField("timeline")} placeholder="Complete by July 4" />
          </Field>
          <Field label="Contractor (optional)">
            <input className="input" value={draft.contractor} onChange={(e) => setField("contractor", e.target.value)} onBlur={() => commitField("contractor")} placeholder="DIY / contractor name + license" />
          </Field>
        </div>
      </div>
    </div>
  );
}

function DetailsReadOnly({ project }) {
  const rows = [
    ["Location", project.location],
    ["Dimensions", project.dimensions],
    ["Materials", project.materials],
    ["Color / finish", project.color],
    ["Timeline", project.timeline],
    ["Contractor", project.contractor],
  ].filter(([_, v]) => v);
  if (rows.length === 0 && !project.description) return null;
  return (
    <div className="card card--pad-lg">
      <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8 }}>Project details</div>
      {project.description && (
        <div style={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.55, marginBottom: 12 }}>{project.description}</div>
      )}
      {rows.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k}><th style={{ textAlign: "left", padding: "4px 12px 4px 0", color: "var(--mute)", fontWeight: 500, width: "30%" }}>{k}</th><td style={{ padding: "4px 0" }}>{v}</td></tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const PB_MAX_IMAGES = 6;

function PhotosCard({ project, canEdit, onChange }) {
  const fileInputRef = useRefPb(null);
  const [busy, setBusy] = useStatePb(false);
  const [error, setError] = useStatePb(null);
  const [kind, setKind] = useStatePb("current");

  const onFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const f of files) {
        if ((project.images || []).length + 1 > PB_MAX_IMAGES) {
          setError(`Max ${PB_MAX_IMAGES} photos.`);
          break;
        }
        await window.Api.projectsUploadImage(project.id, f, kind);
      }
      onChange?.();
    } catch (err) {
      setError(err.message || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (img) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await window.Api.projectsDeleteImage(img.id);
      onChange?.();
    } catch (err) {
      setError(err.message || "Couldn't remove.");
    }
  };

  const images = project.images || [];
  return (
    <div className="card card--pad-lg">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontWeight: 600 }}>Photos</div>
          <div className="text-xs text-mute">Up to {PB_MAX_IMAGES}. Tag each as <i>current</i> (what's there now) or <i>reference</i> (the look you want).</div>
        </div>
        {canEdit && (
          <div className="row" style={{ gap: 8, alignItems: "center" }}>
            <select className="select" value={kind} onChange={(e) => setKind(e.target.value)} disabled={busy} style={{ padding: "6px 10px", fontSize: 13 }}>
              <option value="current">Current state</option>
              <option value="reference">Reference / desired look</option>
            </select>
            <button type="button" className="btn btn--ghost btn--sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy || images.length >= PB_MAX_IMAGES}>
              <Icon name="upload" size={14} />&nbsp; Add photo
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={onFilesPicked} />
          </div>
        )}
      </div>
      {error && <div className="text-sm" style={{ color: "var(--danger)", marginTop: 8 }}>{error}</div>}
      {images.length > 0 ? (
        <div className="row" style={{ gap: 10, marginTop: 12, flexWrap: "wrap" }}>
          {images.map((img) => (
            <div key={img.id} style={{ position: "relative", width: 120, height: 120, borderRadius: 10, overflow: "hidden", border: "1px solid var(--hairline)" }}>
              <ProjectImage imageId={img.id} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              <div style={{ position: "absolute", left: 0, bottom: 0, right: 0, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 11, padding: "3px 6px", textTransform: "capitalize" }}>
                {img.kind}
              </div>
              {canEdit && (
                <button type="button" onClick={() => onRemove(img)}
                  title="Remove image" aria-label="Remove image"
                  style={{ position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,0.65)", color: "#fff", border: 0, fontSize: 12, cursor: "pointer" }}>×</button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-mute" style={{ marginTop: 12 }}>
          No photos yet. Even one "current" + one "reference" picture helps the board picture the change.
        </div>
      )}
    </div>
  );
}

function PolishCard({ project, polishing, onRun, canEdit, error, onClearError, isHouseholdPrimary = true }) {
  const polished = !!project.polished_narrative;
  // Usage info: how many polishes the COMMUNITY has used in the last
  // rolling 30-day window, and when the oldest one rolls off so a slot
  // opens up. Refetched after each polish so the meter updates without
  // a page reload.
  const [usage, setUsage] = useStatePb(null);
  const refreshUsage = () => {
    window.Api.projectsPolishUsage()
      .then(setUsage)
      .catch(() => { /* non-critical — meter just won't render */ });
  };
  useEffectPb(() => { refreshUsage(); }, []);
  // After polish finishes (polishing transitions from true → false),
  // re-pull usage so the meter reflects the new attempt.
  const prevPolishing = useRefPb(polishing);
  useEffectPb(() => {
    if (prevPolishing.current && !polishing) refreshUsage();
    prevPolishing.current = polishing;
  }, [polishing]);

  const atLimit = !!usage && usage.remaining <= 0;
  return (
    <div className="card card--pad-lg" style={{ border: "1px solid var(--accent, #c25e3a)", background: "var(--accent-soft)" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div style={{ maxWidth: 540 }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
            {polished ? "Re-polish with the latest details" : "Have Compass polish your draft"}
          </div>
          <div className="text-sm" style={{ color: "var(--ink)" }}>
            Compass reads your community's bylaws, compares them against your project, and produces
            a board-ready narrative — plus a list of likely concerns and supporting materials you
            should gather before submitting. Takes ~10 seconds.
          </div>
        </div>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => { if (onClearError) onClearError(); onRun(); }}
          disabled={polishing || !canEdit || atLimit || !isHouseholdPrimary}
          title={!isHouseholdPrimary ? "Project Builder is your household primary's seat. Ask them to run polish or transfer primary in Settings → Household." : undefined}
          aria-busy={polishing || undefined}
          style={polishing ? { pointerEvents: "none", opacity: 0.85 } : undefined}
        >
          {polishing
            ? <span className="spinner" style={{ marginRight: 8 }} aria-hidden="true" />
            : null}
          {polishing ? "Polishing…" : (polished ? "Re-run polish" : "Run polish")}
        </button>
      </div>

      {!isHouseholdPrimary && (
        <div className="text-sm" style={{ marginTop: 10, padding: 10, background: "var(--bg-2)", borderRadius: 8, color: "var(--mute)" }}>
          <b style={{ color: "var(--ink)" }}>Primary-only feature.</b> Your household's primary
          account runs AI polish. You can still create the draft and gather photos — ask
          your primary to polish when you're ready, or have them transfer primary to your
          account in <em>Settings → Household</em>.
        </div>
      )}

      {/* Usage meter — appears once we know the user's rolling-hour
          count. Tells them up front "you have N polishes left this
          hour" and when the window rolls forward so they can plan. */}
      {usage && canEdit && (
        <PolishUsageBar usage={usage} onSlotOpened={refreshUsage} />
      )}

      {/* Polish errors render INSIDE the polish card (right under the
          button) so on a phone you don't have to scroll up to see the
          feedback for the action you just took. */}
      {error && !polishing && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: "10px 12px",
            borderRadius: 8,
            background: "var(--danger-soft, #fdecea)",
            color: "var(--danger, #b3261e)",
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}
      {!canEdit && (
        <div className="text-xs text-mute" style={{ marginTop: 8 }}>
          Polish is locked once the project is submitted or decided. Ask the board to send it back if you need to revise.
        </div>
      )}
    </div>
  );
}

function PolishUsageBar({ usage, onSlotOpened }) {
  // Live-updating "resets in N min" so the user doesn't have to
  // refresh the page to see the window roll forward. When the window
  // actually rolls (diffMs <= 0), call onSlotOpened so the parent
  // refetches /polish-usage — otherwise the "Run polish" button stays
  // disabled until the user reloads the page, because `atLimit` is
  // derived from the stale `usage` object.
  const [tick, setTick] = useStatePb(0);
  useEffectPb(() => {
    if (!usage.reset_at) return;
    const i = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(i);
  }, [usage.reset_at]);

  const pct = Math.min(100, Math.round((usage.used / usage.limit) * 100));
  const atLimit = usage.remaining <= 0;
  const nearLimit = !atLimit && usage.remaining <= 2;

  // Format reset_at as a relative phrase ("resets in 23 minutes",
  // "resets at 3:42 PM"). Both fall back gracefully if the date math
  // fails for any reason.
  let resetPhrase = null;
  let windowRolled = false;
  if (usage.reset_at) {
    const resetMs = new Date(usage.reset_at).getTime();
    if (!Number.isNaN(resetMs)) {
      const diffMs = resetMs - Date.now();
      if (diffMs <= 0) {
        resetPhrase = "resets any moment";
        windowRolled = true;
      } else if (diffMs < 60 * 60 * 1000) {
        const mins = Math.max(1, Math.round(diffMs / 60000));
        resetPhrase = `next slot opens in ${mins} minute${mins === 1 ? "" : "s"}`;
      } else {
        const when = new Date(resetMs).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
        resetPhrase = `next slot opens at ${when}`;
      }
    }
  }

  // When the tick observes the window has rolled forward, pull fresh
  // usage so `atLimit` updates and the button re-enables. Guarded by
  // tick in deps so we only fire on the tick that crossed reset_at,
  // not on every render. The parent dedupes via the `usage` setter.
  useEffectPb(() => {
    if (windowRolled && atLimit && typeof onSlotOpened === "function") {
      onSlotOpened();
    }
  }, [windowRolled, atLimit, tick]);

  const fill = atLimit ? "var(--danger)" : (nearLimit ? "var(--accent)" : "color-mix(in oklab, var(--accent, #c25e3a) 60%, transparent)");
  return (
    <div
      // tick used purely to force a re-render each minute so the
      // "resets in N minutes" text stays current
      data-tick={tick}
      style={{ marginTop: 12, fontSize: 13 }}
    >
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span>
          <b>{usage.used}</b> of <b>{usage.limit}</b> polishes used this month (community-wide)
          {resetPhrase && <span style={{ color: "var(--mute)" }}>{" · "}{resetPhrase}</span>}
        </span>
      </div>
      <div style={{ height: 4, marginTop: 6, background: "var(--bg-2)", borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: fill, transition: "width 200ms ease" }} />
      </div>
      {atLimit && (
        <div className="text-xs" style={{ marginTop: 6, color: "var(--danger)" }}>
          Your community has used its monthly polish allowance. The button unlocks as the oldest polish rolls off the 30-day window. The cap is shared across all residents.
        </div>
      )}
    </div>
  );
}

function PolishedOutput({ project, canEdit, onEdit, onReload }) {
  const [editing, setEditing] = useStatePb(false);
  const [draft, setDraft] = useStatePb(project.polished_narrative || "");
  useEffectPb(() => { setDraft(project.polished_narrative || ""); setEditing(false); }, [project.polished_narrative]);

  return (
    <div className="card card--pad-lg">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 16 }}>Board-ready draft</div>
        {onEdit && !editing && (
          <button className="btn btn--ghost btn--sm" onClick={() => setEditing(true)}>
            <Icon name="edit" size={14} /> &nbsp;Edit narrative
          </button>
        )}
      </div>
      <div className="text-xs text-mute" style={{ marginBottom: 10 }}>
        Polished {_relTimePb(project.polished_at)} · Community Compass
      </div>
      {editing ? (
        <div className="stack-sm">
          <textarea className="input" rows={10} value={draft}
            onChange={(e) => setDraft(e.target.value)}
            style={{ resize: "vertical", lineHeight: 1.55 }} />
          <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
            <button className="btn btn--ghost btn--sm" onClick={() => { setDraft(project.polished_narrative || ""); setEditing(false); }}>Cancel</button>
            <button className="btn btn--primary btn--sm" onClick={() => { onEdit(draft); setEditing(false); }}>Save narrative</button>
          </div>
        </div>
      ) : (
        <div style={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.65 }}>{project.polished_narrative}</div>
      )}

      {project.bylaw_findings?.length > 0 && (
        <BylawFindings
          project={project}
          canEdit={canEdit}
          onReload={onReload}
        />
      )}

      {project.likely_concerns?.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Questions the board may ask</div>
          <ul style={{ paddingLeft: 18, margin: 0, fontSize: 14, lineHeight: 1.55 }}>
            {project.likely_concerns.map((c, i) => (
              <li key={i} style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 500 }}>{c.concern}</div>
                <div className="text-sm text-mute" style={{ marginTop: 2 }}>Your answer: {c.suggestion}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {project.missing_checklist?.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Before you submit, gather:</div>
          <ul style={{ paddingLeft: 18, margin: 0, fontSize: 14, lineHeight: 1.55 }}>
            {project.missing_checklist.map((c, i) => (
              <li key={i} style={{ marginBottom: 6 }}>
                <span style={{ fontWeight: 500 }}>{c.item}</span>
                {c.why && <> — <span className="text-sm text-mute">{c.why}</span></>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function _findingHasResponse(saved) {
  if (!saved) return false;
  if (saved.response) return true;
  const answers = saved.answers || [];
  return answers.some((a) => a.value === true || (typeof a.value === "string" && a.value.trim()));
}

function BylawFindings({ project, canEdit, onReload }) {
  const findings = project.bylaw_findings || [];
  const responses = project.finding_responses || {};

  // Keyed by stable finding id (issue #163). Two findings can legitimately
  // share a section — keying by section silently collided. Each finding
  // now carries `id` set by the polish service; legacy projects (saved
  // before the stable-id rollout) still render via the section fallback,
  // but the backend rekeys section-keyed entries to id-keyed on the next
  // polish so the resident's prior answers carry forward.
  const responseCount = findings.reduce(
    (n, f) => _findingHasResponse(responses[f.id] || responses[f.section]) ? n + 1 : n,
    0,
  );

  return (
    <div style={{ marginTop: 18 }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6 }}>
        <div style={{ fontWeight: 600 }}>Bylaw alignment</div>
        {canEdit && responseCount > 0 && (
          <div className="text-xs text-mute">
            <b>{responseCount}</b> of <b>{findings.length}</b> finding{findings.length === 1 ? "" : "s"} addressed
            {" "}— re-polish to apply.
          </div>
        )}
      </div>
      {canEdit && (
        <div className="text-xs text-mute" style={{ marginTop: 2, marginBottom: 6 }}>
          Click a finding to address it. Each one shows the questions Compass needs
          answered for that specific rule — your answers feed the next polish.
        </div>
      )}
      <div className="stack-sm">
        {findings.map((f, i) => (
          <BylawFinding
            key={f.id || `${f.section || "finding"}-${i}`}
            project={project}
            finding={f}
            saved={responses[f.id] || responses[f.section] || null}
            canEdit={canEdit}
            onReload={onReload}
          />
        ))}
      </div>
    </div>
  );
}

// Build the initial form state from the saved answers map.
// Returns { checks: Set<label>, texts: { [label]: string } } so the
// inputs render whatever the resident already typed.
function _initialFormState(saved, complianceActions) {
  const checks = new Set();
  const texts = {};
  const answers = (saved && saved.answers) || [];
  for (const a of answers) {
    if (!a || !a.label) continue;
    if (a.value === true) checks.add(a.label);
    else if (typeof a.value === "string") texts[a.label] = a.value;
  }
  // Make sure every current action label has a slot in `texts` so
  // controlled inputs don't flip from uncontrolled → controlled when
  // the user types.
  for (const action of complianceActions || []) {
    if (action.kind === "text" && texts[action.label] === undefined) {
      texts[action.label] = "";
    }
  }
  return { checks, texts };
}

function BylawFinding({ project, finding, saved, canEdit, onReload }) {
  const tone = {
    compliant: { bg: "color-mix(in oklab, #2d5d4f 12%, transparent)", color: "#2d5d4f", label: "Compliant" },
    non_compliant: { bg: "color-mix(in oklab, #b3261e 12%, transparent)", color: "#b3261e", label: "Non-compliant" },
    needs_clarification: { bg: "color-mix(in oklab, #c89b5a 18%, transparent)", color: "#7a5a2b", label: "Needs clarification" },
  }[finding.compliance] || { bg: "var(--bg-2)", color: "var(--mute)", label: finding.compliance || "Unknown" };

  const complianceActions = finding.compliance_actions || [];
  const hasResponse = _findingHasResponse(saved);
  const initialOpen = canEdit && !hasResponse && (
    finding.compliance === "non_compliant" || finding.compliance === "needs_clarification"
  );
  const [open, setOpen] = useStatePb(initialOpen);

  const initial = _initialFormState(saved, complianceActions);
  const [response, setResponse] = useStatePb(saved?.response || "");
  const [checks, setChecks] = useStatePb(initial.checks);
  const [texts, setTexts] = useStatePb(initial.texts);
  const [busy, setBusy] = useStatePb(false);
  const [error, setError] = useStatePb(null);

  // Re-sync local state when the parent reloads (re-polish brings
  // back a fresh response map AND a potentially-fresh action list).
  useEffectPb(() => {
    const next = _initialFormState(saved, complianceActions);
    setResponse(saved?.response || "");
    setChecks(next.checks);
    setTexts(next.texts);
    // eslint-disable-next-line
  }, [saved?.response, JSON.stringify(saved?.answers || []), JSON.stringify(complianceActions)]);

  const toggleCheck = (label) => {
    setChecks((cur) => {
      const next = new Set(cur);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };
  const setTextValue = (label, value) => {
    setTexts((cur) => ({ ...cur, [label]: value }));
  };

  const buildAnswers = () => {
    const answers = [];
    for (const action of complianceActions) {
      if (action.kind === "check" && checks.has(action.label)) {
        answers.push({ label: action.label, value: true });
      } else if (action.kind === "text") {
        const v = (texts[action.label] || "").trim();
        if (v) answers.push({ label: action.label, value: v });
      }
    }
    // Preserve answers whose labels aren't in the current actions
    // list (e.g. a re-polish renamed them). The backend will carry
    // them forward to Claude on the next polish.
    if (saved?.answers) {
      const currentLabels = new Set(complianceActions.map((a) => a.label));
      for (const a of saved.answers) {
        if (a && a.label && !currentLabels.has(a.label)) {
          if (a.value === true || (typeof a.value === "string" && a.value.trim())) {
            answers.push({ label: a.label, value: a.value });
          }
        }
      }
    }
    return answers;
  };

  const save = async () => {
    if (busy) return;
    // Defensive: legacy projects polished before the stable-id rollout
    // have findings without `id`. The backend would 400 on the route,
    // so we surface a friendly message and ask for a re-polish — that
    // path emits ids and migrates any orphan section-keyed responses.
    if (!finding.id) {
      setError("Re-polish this project to attach answers to specific findings.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await window.Api.projectsUpsertFindingResponse(project.id, finding.id, {
        section: finding.section,
        response,
        answers: buildAnswers(),
      });
      if (onReload) onReload();
    } catch (err) {
      setError(err.message || "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };

  const clearSaved = async () => {
    if (busy) return;
    if (!window.confirm("Remove your saved response for this finding?")) return;
    if (!finding.id) {
      setError("Re-polish this project to attach answers to specific findings.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await window.Api.projectsUpsertFindingResponse(project.id, finding.id, {
        section: finding.section,
        response: "",
        answers: [],
      });
      setResponse("");
      setChecks(new Set());
      setTexts({});
      if (onReload) onReload();
    } catch (err) {
      setError(err.message || "Couldn't clear.");
    } finally {
      setBusy(false);
    }
  };

  const anyContent = !!response.trim() || checks.size > 0 || Object.values(texts).some((v) => (v || "").trim());

  return (
    <div style={{ padding: 10, border: "1px solid var(--hairline)", borderRadius: 10, background: "var(--surface)" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={!canEdit}
        style={{
          width: "100%", background: "transparent", border: 0, padding: 0,
          textAlign: "left", cursor: canEdit ? "pointer" : "default",
        }}
        aria-expanded={open}
      >
        <div className="row" style={{ justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div className="row" style={{ gap: 8, alignItems: "center" }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{finding.section || "Bylaw"}</div>
            {hasResponse && (
              <span className="pill pill--accent" style={{ fontSize: 10, padding: "1px 6px" }}>
                Response added
              </span>
            )}
          </div>
          <div className="row" style={{ gap: 6, alignItems: "center" }}>
            <span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: tone.bg, color: tone.color }}>
              {tone.label}
            </span>
            {canEdit && (
              <Icon
                name="chevron"
                size={12}
                style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)", opacity: 0.6, transition: "transform 120ms ease" }}
              />
            )}
          </div>
        </div>
        <div className="text-sm" style={{ marginTop: 4 }}><b>Requires:</b> {finding.requirement}</div>
        <div className="text-sm" style={{ marginTop: 2 }}><b>Your project:</b> {finding.project_value}</div>
        {finding.reasoning && (
          <div
            className="text-sm"
            style={{
              marginTop: 6,
              padding: "6px 8px",
              borderLeft: `3px solid ${tone.color}`,
              background: tone.bg,
              borderRadius: 4,
              lineHeight: 1.45,
            }}
          >
            <b style={{ color: tone.color }}>Why:</b> {finding.reasoning}
          </div>
        )}
      </button>

      {open && canEdit && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--hairline)" }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
            Address this finding
          </div>
          <div className="text-xs text-mute" style={{ marginBottom: 8 }}>
            Answer the questions Compass surfaced for this rule. The next polish
            uses your answers to re-evaluate compliance.
          </div>

          {/* Dynamic per-finding actions — generated by Compass during
              polish so each finding has questions specific to its rule
              rather than a generic boilerplate list. */}
          {complianceActions.length > 0 && (
            <div className="stack-sm" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {complianceActions.map((action, idx) => {
                if (action.kind === "text") {
                  return (
                    <div key={`${action.label}-${idx}`}>
                      <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>
                        {action.label}
                      </label>
                      <input
                        className="input"
                        type="text"
                        value={texts[action.label] || ""}
                        placeholder={action.hint || ""}
                        onChange={(e) => setTextValue(action.label, e.target.value)}
                        disabled={busy}
                        style={{ fontSize: 13 }}
                      />
                    </div>
                  );
                }
                // default to check
                return (
                  <label
                    key={`${action.label}-${idx}`}
                    className="row"
                    style={{ gap: 8, fontSize: 13, cursor: busy ? "default" : "pointer", alignItems: "flex-start" }}
                  >
                    <input
                      type="checkbox"
                      checked={checks.has(action.label)}
                      onChange={() => toggleCheck(action.label)}
                      disabled={busy}
                      style={{ marginTop: 3 }}
                    />
                    <span>
                      {action.label}
                      {action.hint && (
                        <span className="text-xs text-mute" style={{ display: "block", marginTop: 1 }}>
                          {action.hint}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          {complianceActions.length === 0 && (
            <div className="text-xs text-mute" style={{ marginBottom: 8 }}>
              No specific questions for this rule — Compass thinks you're set.
              Use the notes box below if you want to add anything for the board.
            </div>
          )}

          <div style={{ marginTop: complianceActions.length > 0 ? 12 : 0 }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>
              Notes for this finding {complianceActions.length > 0 && <span className="text-xs text-mute">(optional)</span>}
            </label>
            <textarea
              className="input"
              rows={3}
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder={
                finding.compliance === "non_compliant"
                  ? "Anything else the board should know — e.g. why a variance is reasonable in your case…"
                  : "Extra context the questions above didn't cover…"
              }
              style={{ resize: "vertical", lineHeight: 1.5, fontSize: 13 }}
              maxLength={4000}
              disabled={busy}
            />
          </div>

          {error && (
            <div role="alert" style={{ marginTop: 8, padding: "6px 10px", borderRadius: 6, background: "var(--danger-soft, #fdecea)", color: "var(--danger, #b3261e)", fontSize: 12 }}>
              {error}
            </div>
          )}
          <div className="row" style={{ justifyContent: "space-between", marginTop: 10, gap: 8, flexWrap: "wrap" }}>
            <div>
              {hasResponse && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={clearSaved}
                  disabled={busy}
                  style={{ color: "var(--danger)" }}
                >
                  Clear response
                </button>
              )}
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setOpen(false)}
                disabled={busy}
              >
                Collapse
              </button>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={save}
                disabled={busy || (!anyContent && !hasResponse)}
              >
                {busy ? "Saving…" : "Save response"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DownloadCard({ project }) {
  // Phased state machine instead of a binary busy flag — slow
  // server-side renders (Pillow + python-docx + photo embedding)
  // can take several seconds and the user needs to see what's
  // happening so they don't click a second time and confuse the
  // browser into "resume download?" prompts.
  //
  // idle → preparing (request in flight)
  //      → downloading (blob received, browser save kicked off)
  //      → saved (3s success state, then back to idle)
  //      → error (with retry)
  const [phase, setPhase] = useStatePb("idle");
  const [error, setError] = useStatePb(null);

  const polishMissing = [
    !project.polished_narrative && "narrative",
    !(project.bylaw_findings?.length) && "bylaw alignment",
    !(project.likely_concerns?.length) && "likely concerns",
    !(project.missing_checklist?.length) && "checklist",
  ].filter(Boolean);
  const anyPolish = polishMissing.length < 4;

  // Auto-reset the "saved" state after a few seconds so the button
  // becomes available again. We don't auto-reset "error" — the user
  // should see what failed until they retry.
  useEffectPb(() => {
    if (phase !== "saved") return;
    const t = setTimeout(() => setPhase("idle"), 4000);
    return () => clearTimeout(t);
  }, [phase]);

  const onDownload = async () => {
    if (phase !== "idle" && phase !== "error") return;  // guard against double-clicks
    setPhase("preparing");
    setError(null);
    try {
      const { blob, filename } = await window.Api.projectsDownload(project.id);
      setPhase("downloading");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setPhase("saved");
    } catch (err) {
      setError(err.message || "Download failed.");
      setPhase("error");
    }
  };

  const isWorking = phase === "preparing" || phase === "downloading";
  const buttonLabel = (() => {
    if (phase === "preparing") return "Preparing your packet…";
    if (phase === "downloading") return "Saving to your device…";
    if (phase === "saved") return "Saved!";
    if (phase === "error") return "Try again";
    return "Download a copy";
  })();
  const statusLine = (() => {
    if (phase === "preparing") return "Building your Word document on the server. This usually takes a few seconds.";
    if (phase === "downloading") return "Almost there — handing the file to your browser.";
    if (phase === "saved") return "Check your Downloads folder for the file.";
    return null;
  })();

  return (
    <div className="card card--pad-lg">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div style={{ maxWidth: 540 }}>
          <div style={{ fontWeight: 600 }}>Download a copy</div>
          <div className="text-sm text-mute" style={{ marginTop: 2 }}>
            {anyPolish
              ? "Download a Word document with everything in this project — narrative, bylaw findings, photos, and the board-question prep."
              : "You can download a copy any time. Until you run Compass polish, the polished sections will appear as called-out gaps in the doc."}
          </div>
          {polishMissing.length > 0 && polishMissing.length < 4 && (
            <div className="text-xs" style={{ marginTop: 6, color: "var(--mute)" }}>
              Not yet polished: <i>{polishMissing.join(", ")}</i> — these sections will show as callouts in the doc.
            </div>
          )}
        </div>
        {/* pointer-events: none on top of disabled so a misbehaving
            browser can't fire a second click while we're in flight. */}
        <button
          className="btn btn--primary"
          onClick={onDownload}
          disabled={isWorking}
          aria-busy={isWorking || undefined}
          style={isWorking ? { pointerEvents: "none", opacity: 0.85 } : undefined}
        >
          {isWorking
            ? <span className="spinner" style={{ marginRight: 8 }} aria-hidden="true" />
            : <Icon name={phase === "saved" ? "check" : "download"} size={14} />}
          &nbsp;{buttonLabel}
        </button>
      </div>
      {statusLine && (
        <div
          className="text-sm"
          role={phase === "saved" ? "status" : undefined}
          aria-live="polite"
          style={{
            marginTop: 10,
            padding: "8px 10px",
            borderRadius: 8,
            background: phase === "saved" ? "color-mix(in oklab, #2d5d4f 10%, transparent)" : "var(--bg-2)",
            color: phase === "saved" ? "#2d5d4f" : "var(--ink)",
            display: "flex", alignItems: "center", gap: 8,
          }}
        >
          {isWorking && <span className="spinner" aria-hidden="true" />}
          {phase === "saved" && <Icon name="check" size={14} />}
          <span>{statusLine}</span>
        </div>
      )}
      {error && (
        <div role="alert" style={{ marginTop: 10, padding: "8px 10px", borderRadius: 8, background: "var(--danger-soft, #fdecea)", color: "var(--danger, #b3261e)" }}>
          {error}
        </div>
      )}
    </div>
  );
}

function BoardActions({ project, onAction, busy }) {
  const [notes, setNotes] = useStatePb(project.board_decision_notes || "");
  useEffectPb(() => { setNotes(project.board_decision_notes || ""); }, [project.id, project.board_decision_notes]);

  const act = (status) => onAction({ status, board_decision_notes: notes || null });

  return (
    <div className="card card--pad-lg">
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Board actions</div>
      <div className="text-sm text-mute" style={{ marginBottom: 8 }}>
        Add optional notes the resident will see — required when sending back for changes.
      </div>
      <textarea className="input" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
        placeholder="Optional notes for the resident…"
        style={{ resize: "vertical", lineHeight: 1.5 }} />
      <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        {project.status !== "in_review" && (
          <button className="btn btn--ghost btn--sm" disabled={busy} onClick={() => act("in_review")}>Move to in-review</button>
        )}
        <button className="btn btn--ghost btn--sm" disabled={busy || !notes.trim()} onClick={() => act("needs_changes")} title={!notes.trim() ? "Add notes first" : undefined}>
          Send back for changes
        </button>
        <button className="btn btn--primary btn--sm" disabled={busy} onClick={() => act("approved")}>Approve</button>
        <button className="btn btn--ghost btn--sm" disabled={busy} onClick={() => act("rejected")} style={{ color: "var(--danger)" }}>Reject</button>
      </div>
    </div>
  );
}

export { ProjectBuilder };
